import type { AddressField, NewShippingAddress, ShippingAddress } from '../api/shipping-addresses'

/**
 * Addresses as the form takes them and the row shows them. The shop ships
 * within the United States only, so the form offers the states and nothing
 * else; a state is its full name, as Recharge wants it.
 */

export const US_STATES: readonly string[] = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
]

/** "1 Main Street, Apt 2, Chicago, Illinois 60601": the address on one line. */
export function formatAddress(
  address: Pick<ShippingAddress, 'address1' | 'address2' | 'city' | 'province' | 'zip'>,
): string {
  return [address.address1, address.address2, `${address.city}, ${address.province} ${address.zip}`]
    .filter(Boolean)
    .join(', ')
}

export const EMPTY_ADDRESS: NewShippingAddress = {
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  city: '',
  province: '',
  zip: '',
  phone: '',
}

const ZIP = /^\d{5}(-\d{4})?$/

const REQUIRED: readonly [AddressField, string][] = [
  ['firstName', 'Enter a first name.'],
  ['lastName', 'Enter a last name.'],
  ['address1', 'Enter a street address.'],
  ['city', 'Enter a city.'],
  ['province', 'Choose a state.'],
  ['zip', 'Enter a ZIP code.'],
]

export interface AddressProblem {
  field: AddressField
  message: string
}

/** The first thing missing or malformed, or null when the address can be sent. */
export function validateAddress(address: NewShippingAddress): AddressProblem | null {
  for (const [field, message] of REQUIRED) {
    if (address[field].trim() === '') return { field, message }
  }
  if (!ZIP.test(address.zip.trim())) return { field: 'zip', message: 'Enter a valid ZIP code.' }
  return null
}
